import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Badge,
  Avatar,
  Chip,
  Grid,
  IconButton,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Autocomplete,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AccountTree as TreeIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Search as SearchIcon,
  RestartAlt as ResetIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { hasReadPermission } from '../../utils/menuFilter';
import { useUserState } from '../../context/UserContext';
import RefreshButton from '../../components/RefreshButton/RefreshButton';

// --- Color Palette Configuration ---
const LEVEL_COLORS = {
  L1: '#e91e63', // Pink
  L2: '#ff9800', // Orange
  L3: '#2196f3', // Blue
  L4: '#4caf50', // Green
  L5: '#9c27b0', // Purple
  DEFAULT: '#757575', // Grey
};

// Global Profile Avatar Color
const PROFILE_AVATAR_COLOR = '#F5E7C6';

const getLevelColor = (level) => LEVEL_COLORS[level] || LEVEL_COLORS.DEFAULT;

// --- HELPER: Normalize API Data (_id -> id) ---
const normalizeNodeData = (node, parentId = null, depth = 0) => {
  if (!node) return null;
  const id = node._id || node.id || Math.random().toString(36).slice(2);

  return {
    ...node,
    id: id,
    loginId: node.loginId || '',
    sponsorLoginId: node.sponsorLoginId || '',
    associateId: node.associateId || node.associateIdDisplay || `ASSO-${id.toString().slice(-4)}`,
    name: node.name || 'Associate',
    level: node.level || `L${Math.min(depth + 1, 5)}`,
    parentId: parentId,
    children: Array.isArray(node.children)
      ? node.children.map(child => normalizeNodeData(child, id, depth + 1))
      : [],
    isChildren: Boolean(node.isChildren || (node.children && node.children.length > 0)),
    role: node.role || '',
    designation: node.designation || '',
    phone: node.phone || '',
    whatsapp: node.whatsapp || node.phone || '',
    city: node.city || '',
    state: node.state || '',
  };
};

// --- Nested Tree Modal Component ---
const NestedTreeModal = ({
  associate,
  closeModal,
  findNode,
  MAX_DEPTH,
  initialLevel = 0,
  onViewDetails,
}) => {
  const [loadingMoreIds, setLoadingMoreIds] = useState(new Set());
  const [expandedChildIds, setExpandedChildIds] = useState(new Set());
  const [expandedBeyondLimit, setExpandedBeyondLimit] = useState(new Set());
  const [nestedModalData, setNestedModalData] = useState(null);
  const [localAssociateData, setLocalAssociateData] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (associate && associate.id) {
      setLocalAssociateData([associate]);
      setExpandedChildIds(new Set([associate.id]));
    }
  }, [associate]);

  const toggleChildExpand = (id) => {
    setExpandedChildIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateNodeInTree = (nodes, targetId, updateFn) => {
    return nodes.map((node) => {
      if (String(node.id) === String(targetId)) {
        return updateFn(node);
      }
      if (Array.isArray(node.children) && node.children.length > 0) {
        return {
          ...node,
          children: updateNodeInTree(node.children, targetId, updateFn),
        };
      }
      return node;
    });
  };

  const handleShowMoreInsideModal = async (node, absoluteLevel) => {
    if (!node?.id) return;
    const id = node.id;
    setLoadingMoreIds((prev) => new Set(prev).add(id));

    try {
      const baseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
      const response = await axios.get(`${baseUrl}/api/associates/nestedTree?id=${String(id)}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': '', // Override interceptor
        },
      });
      const body = response.data || {};
      const rawRoot = Array.isArray(body.tree) ? body.tree[0] : body.tree;

      if (rawRoot) {
        const normalizedRoot = normalizeNodeData(rawRoot, node.parentId, absoluteLevel);

        setLocalAssociateData((prevTree) => {
          return updateNodeInTree(prevTree, id, (n) => ({
            ...n,
            children: normalizedRoot.children,
            isChildren: normalizedRoot.isChildren,
          }));
        });
        setNestedModalData(normalizedRoot);
      }
      setExpandedBeyondLimit((prev) => new Set(prev).add(id));
      setExpandedChildIds((prev) => new Set(prev).add(id));
    } catch (err) {
      console.error('Failed to load deeper levels:', err);
    } finally {
      setLoadingMoreIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const renderModalHierarchyLevel = (node, level = 0) => {
    if (!node) return null;

    const absoluteLevel = initialLevel + level;
    const atDepthLimit = level >= MAX_DEPTH - 1;

    const isExpanded = expandedChildIds.has(node.id);
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const indent = level * (isMobile ? 12 : 30);

    const beyondLimitExpanded = expandedBeyondLimit.has(node.id);
    const shouldShowMorePrompt = (atDepthLimit && node.isChildren && !beyondLimitExpanded);

    const nodeColor = PROFILE_AVATAR_COLOR;
    const levelBadgeColor = getLevelColor(node.level);

    return (
      <Box key={node.id} sx={{ mb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            pl: `${indent}px`,
            py: 0.75,
            borderRadius: 1,
            bgcolor: 'background.paper',
            border: '1px solid rgba(0,0,0,0.08)',
            transition: 'background-color 0.2s',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' }
          }}
        >
          {(hasChildren || shouldShowMorePrompt) ? (
            <Box
              sx={{
                minWidth: 24,
                display: 'flex',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'text.secondary',
              }}
              onClick={(e) => {
                e.stopPropagation();
                toggleChildExpand(node.id);
              }}
            >
              <ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
            </Box>
          ) : <Box sx={{ minWidth: 24 }} />}

          <Avatar
            sx={{
              bgcolor: nodeColor,
              color: '#5d4037',
              width: 30,
              height: 30,
              fontSize: '0.8rem',
              cursor: 'pointer',
              fontWeight: 'bold',
              border: '1px solid rgba(0,0,0,0.1)',
              '&:hover': { opacity: 0.8 },
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onViewDetails) onViewDetails(node);
            }}
          >
            {node.name ? node.name.charAt(0) : '?'}
          </Avatar>

          <Box
            sx={{ flex: 1, cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              if (onViewDetails) onViewDetails(node);
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ color: 'text.primary' }}>
                {node.name}
              </Typography>
              <Chip
                label={node.level || `L${absoluteLevel + 1}`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  bgcolor: levelBadgeColor,
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
              {(node.directTeamCount > 0 || node.totalTeamCount > 0) && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Chip
                    label={isMobile ? `D:${node.directTeamCount || 0}` : `${node.directTeamCount || 0} Direct`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 16, fontSize: '0.55rem', borderColor: levelBadgeColor, color: levelBadgeColor }}
                  />
                  <Chip
                    label={isMobile ? `I:${node.indirectTeamCount || 0}` : `${node.indirectTeamCount || 0} Indirect`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 16, fontSize: '0.55rem', borderColor: levelBadgeColor, color: levelBadgeColor }}
                  />
                  <Chip
                    label={isMobile ? `T:${node.totalTeamCount || 0}` : `${node.totalTeamCount || 0} Total`}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: '0.55rem',
                      bgcolor: alpha(levelBadgeColor, 0.1),
                      color: levelBadgeColor,
                      borderColor: levelBadgeColor,
                      border: '1px solid',
                    }}
                  />
                </Box>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {node.loginId || node.associateId}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (onViewDetails) onViewDetails(node);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Box>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 0.5 }}>
              {node.children.map((child) => renderModalHierarchyLevel(child, level + 1))}
            </Box>
          </Collapse>
        )}

        {shouldShowMorePrompt && isExpanded && (
          <Box sx={{ pl: `${(level + 1) * (isMobile ? 12 : 30)}px`, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              sx={{ borderColor: levelBadgeColor, color: levelBadgeColor }}
              onClick={(e) => {
                e.stopPropagation();
                handleShowMoreInsideModal(node, absoluteLevel);
              }}
              disabled={loadingMoreIds.has(node.id)}
            >
              {loadingMoreIds.has(node.id) ? 'Loading...' : 'Show more'}
            </Button>
          </Box>
        )}

        {nestedModalData && (
          // Recursive Modal also updated to not be full screen
          <Dialog
            open={Boolean(nestedModalData)}
            onClose={() => setNestedModalData(null)}
            fullWidth
            maxWidth="xl"
            PaperProps={{
              sx: { height: '90vh', borderRadius: 2 }
            }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
              <Typography variant="h6">Deep Dive: {nestedModalData.name || 'Branch'} </Typography>
              <IconButton onClick={() => setNestedModalData(null)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ maxHeight: '70vh', overflowY: 'auto', p: 1 }}>
                <NestedTreeModal
                  associate={nestedModalData}
                  closeModal={() => setNestedModalData(null)}
                  findNode={findNode}
                  MAX_DEPTH={MAX_DEPTH}
                  initialLevel={absoluteLevel + 1}
                  onViewDetails={onViewDetails}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNestedModalData(null)} variant="outlined" color="inherit">
                Back to previous view
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    );
  };

  return <Box>{localAssociateData.length > 0 && renderModalHierarchyLevel(localAssociateData[0], 0)}</Box>;
};

// --- Main AssociateTree Component ---
const AssociateTree = () => {
  const history = useHistory();
  const { currentUser } = useUserState();
  const canRead = hasReadPermission('associate.associate-tree') || hasReadPermission('associate-tree') || hasReadPermission('associate');
  const [associateData, setAssociateData] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [treeError, setTreeError] = useState('');
  const MAX_DEPTH = 5;
  const [openModalAssociate, setOpenModalAssociate] = useState(null);
  const [expandedChildIds, setExpandedChildIds] = useState(new Set());

  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedSearchValue, setSelectedSearchValue] = useState(null);

  const [openDetails, setOpenDetails] = useState(false);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [modalLoadingNodeId, setModalLoadingNodeId] = useState(null);
  const [teamStats, setTeamStats] = useState({
    direct: 0,
    indirect: 0,
    total: 0
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const toggleChildExpand = (id) => {
    setExpandedChildIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const normalizeTree = (nodes, parentId = null, depth = 0) => {
    if (!Array.isArray(nodes)) return [];
    const levelLabel = `L${Math.min(depth + 1, 5)}`;
    const normalized = [];
    nodes.forEach((node) => {
      const id = node?._id || node?.id || node?.associateId || Math.random().toString(36).slice(2);
      const current = {
        ...node, // Maintain all original fields from API including totalTeamCount, etc.
        id,
        associateId: node?.associateId || node?.associateIdDisplay || `ASSO-${id?.toString().slice(-4)}`,
        name: node?.name || 'Associate',
        level: node?.level || levelLabel,
        parentId,
        children: Array.isArray(node?.children) ? normalizeTree(node.children, id, depth + 1) : [],
        isChildren: Boolean(node?.isChildren) || (Array.isArray(node?.children) && node.children.length > 0),
        role: node?.role || '',
        designation: node?.designation || '',
        phone: node?.phone || '',
        whatsapp: node?.whatsapp || node?.phone || '',
        city: node?.city || '',
        state: node?.state || '',
        registrationDate: node?.registrationDate || '',
        credentials: node?.credentials || {},
      };
      normalized.push(current);
    });
    return normalized;
  };

  const fetchSearchOptions = async () => {
    setSearchLoading(true);
    try {
      const baseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
      const response = await axios.get(`${baseUrl}/api/associates?page=1&limit=10000`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const body = response.data || {};
      if (body.status && Array.isArray(body.data)) {
        setSearchOptions(body.data);
      } else {
        setSearchOptions([]);
      }
    } catch (err) {
      console.error("Failed to load search options", err);
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchTree = async (manualId = null) => {
    try {
      setLoadingTree(true);
      setTreeError('');

      // Find the best source for current user ID
      let myId = currentUser?._id || currentUser?.id;

      const baseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

      // Optional: Try to get the profile ID for logging or state fallback
      try {
        const profileResponse = await axios.get(`${baseUrl}/api/associates/profileDetail`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': '', // Override interceptor
          },
        });
        const profile = profileResponse.data?.data || profileResponse.data;
        if (profile?._id || profile?.id) {
          myId = profile._id || profile.id;
        }
      } catch (profileErr) {
        // Silently continue
      }

      const queryParams = new URLSearchParams(window.location.search);
      const urlId = queryParams.get('id');
      const finalId = manualId || urlId || myId;

      console.log("Fetching tree for Associate ID:", finalId);

      const response = await axios.get(`${baseUrl}/api/associates/nestedTree?id=${finalId || ''}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': '', // Override interceptor
        },
      });
      const body = response.data || {};

      if (!(body.success || body.status === 200 || body.status === "true" || body.status === true)) {
        setTreeError(body.message || 'Failed to retrieve hierarchy data.');
        setLoadingTree(false);
        return;
      }

      const treeRaw = Array.isArray(body?.tree) ? body.tree : body?.tree ? [body.tree] : [];

      // Update team stats from root node
      const rootNode = Array.isArray(body?.tree) ? body.tree[0] : body?.tree;
      if (rootNode) {
        setTeamStats({
          direct: rootNode.directTeamCount || 0,
          indirect: rootNode.indirectTeamCount || 0,
          total: rootNode.totalTeamCount || 0
        });
      }

      const recursivelyFlatten = (nodes, parentId = null, depth = 0) => {
        let flattened = [];
        nodes.forEach(node => {
          const normalizedNode = normalizeTree([node], parentId, depth)[0];
          flattened.push({ ...normalizedNode, children: [] });
          if (Array.isArray(node.children)) {
            flattened = [...flattened, ...recursivelyFlatten(node.children, normalizedNode.id, depth + 1)];
          }
        });
        return flattened;
      }
      const flattened = recursivelyFlatten(treeRaw);
      setAssociateData(flattened);

      if (flattened.length > 0) {
        setExpandedChildIds(new Set([flattened[0].id]));
      }

    } catch (err) {
      console.error("AssociateTree fetchTree error:", err);
      // Extract specific backend error message or use a more descriptive default
      const backendMsg = err?.response?.data?.message || err?.message || 'Error occurred while loading team';
      const statusDetails = err?.response?.status ? ` (Status: ${err.response.status})` : '';
      setTreeError(`${backendMsg}${statusDetails}`);
      setAssociateData([]);
    } finally {
      setLoadingTree(false);
    }
  };

  const handleRefresh = async () => {
    setSelectedSearchValue(null);
    await Promise.all([
      fetchTree(null),
      fetchSearchOptions()
    ]);
  };

  useEffect(() => {
    fetchTree();
    fetchSearchOptions();
  }, [currentUser?.id, currentUser?._id]);

  const handleSearchSelect = (event, newValue) => {
    setSelectedSearchValue(newValue);
    if (newValue && newValue._id) {
      fetchTree(newValue._id);
    } else if (!newValue) {
      fetchTree(null);
    }
  };

  const handleResetSearch = () => {
    setSelectedSearchValue(null);
    fetchTree(null);
  };

  const buildTree = () => {
    const map = new Map();
    associateData.forEach((a) => map.set(String(a.id), { ...a, children: [] }));
    const roots = [];
    associateData.forEach((a) => {
      const parentKey = a.parentId ? String(a.parentId) : null;
      const currentNode = map.get(String(a.id));
      if (!parentKey || !currentNode) {
        if (currentNode) roots.push(currentNode);
      } else {
        const parent = map.get(parentKey);
        if (parent) parent.children.push(currentNode);
        else roots.push(currentNode);
      }
    });
    return roots;
  };

  const associateTree = buildTree();
  const getAssociateById = (id) => associateData.find((a) => String(a.id) === String(id)) || null;
  const findNode = (id) => getAssociateById(id);

  const handleViewDetails = (node) => {
    console.log('hi', node);
    if (!node) return;
    setSelectedAssociate(node);

    setOpenDetails(true);
  };

  const hasMatchInSubtree = (node) => {
    if (!node) return false;
    return true;
  };

  const handleShowMoreClick = async (node) => {
    if (!node?.id) return;
    setModalLoadingNodeId(node.id);
    try {
      const baseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const response = await axios.get(`${baseUrl}/api/associates/nestedTree?id=${String(node.id)}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': '', // Override interceptor
        },
      });
      const body = response.data || {};
      const rawRoot = Array.isArray(body.tree) ? body.tree[0] : body.tree;

      if (rawRoot) {
        const normalizedRoot = normalizeNodeData(rawRoot, node.parentId, 0);
        setOpenModalAssociate(normalizedRoot);
      }
    } catch (err) {
      console.error("Failed to load modal tree:", err);
    } finally {
      setModalLoadingNodeId(null);
    }
  };

  const renderHierarchyLevel = (node, level = 0) => {
    const fullNode = getAssociateById(node.id);
    const childrenCount = fullNode ? associateData.filter(a => String(a.parentId) === String(fullNode.id)).length : 0;
    const isExpanded = expandedChildIds.has(node.id);
    const hasChildren = childrenCount > 0;
    const indent = level * (isMobile ? 16 : 48);
    const atDepthLimit = level >= MAX_DEPTH - 1;
    const isChildrenFlagTrue = fullNode?.isChildren;
    const showMorePrompt = atDepthLimit && (childrenCount > 0 || isChildrenFlagTrue);
    const visibleChildren = hasChildren ? node.children : [];
    const showChildren = !showMorePrompt && visibleChildren.length > 0;

    const nodeColor = PROFILE_AVATAR_COLOR;
    const levelBadgeColor = getLevelColor(node.level);

    return (
      <Box key={node.id} sx={{ mb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            pl: `${indent}px`,
            py: 0.75,
            borderRadius: 1,
            // Main Tree - White/Transparent background
            bgcolor: 'background.paper',
            border: '1px solid rgba(0,0,0,0.08)',
            transition: 'background-color 0.2s',
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.02)',
            }
          }}
        >
          {(hasChildren || showMorePrompt) ? (
            <Box
              sx={{
                minWidth: 24,
                display: 'flex',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'text.secondary',
              }}
              onClick={(e) => {
                e.stopPropagation();
                toggleChildExpand(node.id);
              }}
            >
              <ExpandMoreIcon
                sx={{
                  transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                }}
              />
            </Box>
          ) : <Box sx={{ minWidth: 24 }} />}

          <Avatar
            sx={{
              bgcolor: nodeColor,
              color: '#5d4037',
              width: 36,
              height: 36,
              fontSize: '0.875rem',
              cursor: 'pointer',
              fontWeight: 'bold',
              border: '1px solid rgba(0,0,0,0.1)',
              '&:hover': { opacity: 0.8 },
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(findNode(node.id));
            }}
          >
            {node.name.charAt(0)}
          </Avatar>

          <Box
            sx={{ flex: 1, cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(findNode(node.id));
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" fontWeight="bold" sx={{ color: 'text.primary' }}>
                {node.name}
              </Typography>
              <Chip
                label={node.level}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: levelBadgeColor,
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
              {(fullNode?.directTeamCount > 0 || fullNode?.totalTeamCount > 0) && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Chip
                    label={isMobile ? `D:${fullNode.directTeamCount || 0}` : `${fullNode.directTeamCount || 0} Direct`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.6rem', borderColor: levelBadgeColor, color: levelBadgeColor }}
                  />
                  <Chip
                    label={isMobile ? `I:${fullNode.indirectTeamCount || 0}` : `${fullNode.indirectTeamCount || 0} Indirect`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.6rem', borderColor: levelBadgeColor, color: levelBadgeColor }}
                  />
                  <Chip
                    label={isMobile ? `T:${fullNode.totalTeamCount || 0}` : `${fullNode.totalTeamCount || 0} Total`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      bgcolor: alpha(levelBadgeColor, 0.1),
                      color: levelBadgeColor,
                      borderColor: levelBadgeColor,
                      border: '1px solid',
                    }}
                  />
                </Box>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {node.loginId || node.associateId} • {node.designation}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(findNode(node.id));
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Box>

        {showChildren && (
          <Collapse in={isExpanded} timeout={0} unmountOnExit>
            <Box sx={{ mt: 0.5 }}>
              {visibleChildren.map((child) => renderHierarchyLevel(child, level + 1))}
            </Box>
          </Collapse>
        )}

        {showMorePrompt && isExpanded && (
          <Box sx={{ pl: `${(level + 1) * (isMobile ? 16 : 48)}px`, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              sx={{ bgcolor: levelBadgeColor, '&:hover': { bgcolor: alpha(levelBadgeColor, 0.9) } }}
              onClick={(e) => {
                e.stopPropagation();
                handleShowMoreClick(node);
              }}
              disabled={modalLoadingNodeId === node.id}
            >
              {modalLoadingNodeId === node.id ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Loading...
                </Box>
              ) : 'Show more'}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const visibleRoots = associateTree.filter((root) => hasMatchInSubtree(root));

  if (!canRead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Associate Hierarchy. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Badge badgeContent={associateData.length} color="primary">
            <TreeIcon sx={{ fontSize: { xs: 32, md: 40 }, color: 'primary.main' }} />
          </Badge>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', md: '2.125rem' } }}>Associate Hierarchy</Typography>
            <Typography variant="body2" color="text.secondary">Visualize the associate hierarchy</Typography>
          </Box>
        </Box>
        <RefreshButton onClick={handleRefresh} loading={loadingTree} />
      </Box>

      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
            Associate Hierarchy Tree
          </Typography>
          <Chip
            label={`Tree displays ${MAX_DEPTH} levels by default. Use "Show more" to explore deeper branches.`}
            color="primary"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          {/* Legend */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, p: 1.5, borderRadius: 1, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1, display: 'flex', alignItems: 'center' }}>
              Level Legend:
            </Typography>
            {Object.entries(LEVEL_COLORS).filter(([key]) => key !== 'DEFAULT').map(([level, color]) => (
              <Box key={level} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: color, border: '1px solid rgba(0,0,0,0.1)' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{level}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {treeError && <Typography color="error" sx={{ mb: 1 }}>{treeError}</Typography>}

        {/* Filters & SEARCH Section */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { xs: 'stretch', md: 'center' }, mb: 2, flexWrap: 'wrap' }}>

          {/* 1. Searchable Dropdown (Autocomplete) */}
          <Autocomplete
            options={searchOptions}
            loading={searchLoading}
            value={selectedSearchValue}
            onChange={handleSearchSelect}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            sx={{ minWidth: { xs: '100%', md: 300 }, flex: { xs: 'none', md: 0 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Search Associate by Name/ID"
                placeholder="Type name..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option._id}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {option.email}
                  </Typography>
                  <Typography variant="caption" color="primary">
                    ID: {option.userAccess?.loginId || option.associateId}
                  </Typography>
                </Box>
              </Box>
            )}
          />

          {/* 2. Reset Button */}
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleResetSearch}
            startIcon={<ResetIcon />}
            sx={{ height: 40, width: { xs: '100%', md: 'auto' } }}
          >
            Reset
          </Button>

          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
          <Button variant="contained" onClick={() => history.push('/app/user/AssociateRegistration')} fullWidth={isMobile}>
            Add Associate
          </Button>
        </Box>

        {/* Tree Container */}
        <Box sx={{ maxHeight: { xs: '50vh', sm: '60vh', md: '70vh' }, overflowY: 'auto', overflowX: 'hidden', p: 1 }}>
          {loadingTree ? (
            <Typography variant="body2" color="text.secondary">Loading associate hierarchy...</Typography>
          ) : visibleRoots.length > 0 ? (
            visibleRoots.map((root) => (
              <Box key={root.id}>
                {renderHierarchyLevel(root, 0)}
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">No associates found.</Typography>
          )}
        </Box>
      </Paper>

      {/* --- Deep Dive Modal --- */}
      {openModalAssociate && (
        <Dialog
          open={Boolean(openModalAssociate)}
          onClose={() => setOpenModalAssociate(null)}
          fullWidth
          maxWidth="xl"
          PaperProps={{
            sx: { height: '90vh', borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: getLevelColor(openModalAssociate.level),
            color: 'white'
          }}>
            <Typography variant="h5" fontWeight="bold">
              Deep Dive Hierarchy: {openModalAssociate.name} Branch
            </Typography>
            <IconButton onClick={() => setOpenModalAssociate(null)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ p: 2 }}>
              {/* Modal Legend */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, opacity: 0.8 }}>
                {Object.entries(LEVEL_COLORS).filter(([key]) => key !== 'DEFAULT').map(([level, color]) => (
                  <Box key={level} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color }} />
                    <Typography variant="caption">{level}</Typography>
                  </Box>
                ))}
              </Box>

              <NestedTreeModal
                associate={openModalAssociate}
                closeModal={() => setOpenModalAssociate(null)}
                findNode={findNode}
                MAX_DEPTH={MAX_DEPTH}
                initialLevel={openModalAssociate.level ? (parseInt(openModalAssociate.level.replace('L', '')) - 1) : 0}
                onViewDetails={handleViewDetails}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ bgcolor: 'background.paper' }}>
            <Button onClick={() => setOpenModalAssociate(null)} variant="contained" startIcon={<TreeIcon />}>
              Back to full tree
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Details Dialog */}
      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Associate Details
          <IconButton onClick={() => setOpenDetails(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedAssociate ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold">{selectedAssociate.name}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Chip label={`Login ID: ${selectedAssociate.loginId || '-'}`} size="small" color="primary" variant="outlined" />
                  <Chip label={selectedAssociate.level} size="small" sx={{ bgcolor: getLevelColor(selectedAssociate.level), color: 'white', fontWeight: 'bold' }} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Role/Designation</Typography>
                <Typography variant="body1" fontWeight="medium">{selectedAssociate.designation || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Sponsor Login ID</Typography>
                <Typography variant="body1" fontWeight="medium">{selectedAssociate.sponsorLoginId || ''}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon sx={{ fontSize: 16 }} color="primary" />
                  <Typography variant="body1">{selectedAssociate.phone || '-'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">WhatsApp</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WhatsAppIcon sx={{ fontSize: 16 }} color="success" />
                  <Typography variant="body1">{selectedAssociate.whatsapp || '-'}</Typography>
                </Box>
              </Grid>
              {/* <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Commission (%)</Typography>
                <Typography variant="body1" fontWeight="bold" color="primary">{selectedAssociate.commission ?? '-'}%</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Associate No.</Typography>
                <Typography variant="body1">{selectedAssociate.associateId || '-'}</Typography>
              </Grid> */}
            </Grid>
          ) : <Typography>No associate selected.</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssociateTree;